import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { DotsThreeVertical, PencilSimple, Trash, X } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface WebpageActionsFabProps {
  onEdit: () => void;
  onDelete: () => void;
}

export function WebpageActionsFab({ onEdit, onDelete }: WebpageActionsFabProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleEdit = useCallback(() => {
    setIsExpanded(false);
    onEdit();
  }, [onEdit]);

  const handleDelete = useCallback(() => {
    setIsExpanded(false);
    onDelete();
  }, [onDelete]);

  // Spacing from main button center
  const buttonOffset = 60; // px

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Edit button - positioned above main button (primary blue) */}
      <Button
        className={cn(
          "absolute h-12 w-12 rounded-full p-0 shadow-lg transition-all duration-200 ease-out",
          "bg-primary text-primary-foreground hover:bg-blue-600 active:bg-blue-700",
          isExpanded 
            ? "opacity-100" 
            : "opacity-0 pointer-events-none"
        )}
        style={{ 
          right: 4,
          bottom: buttonOffset + 4,
          transform: isExpanded ? 'translateY(0)' : 'translateY(16px)',
        }}
        onClick={handleEdit}
        aria-label="Edit webpage"
      >
        <PencilSimple className="h-5 w-5 text-white" weight="bold" />
      </Button>
      
      {/* Delete button - positioned to the left of main button (red) */}
      <Button
        variant="secondary"
        className={cn(
          "absolute h-12 w-12 rounded-full p-0 shadow-lg transition-all duration-200 ease-out",
          "bg-red-100 hover:bg-red-200",
          isExpanded 
            ? "opacity-100" 
            : "opacity-0 pointer-events-none"
        )}
        style={{ 
          right: buttonOffset + 4,
          bottom: 4,
          transform: isExpanded ? 'translateX(0)' : 'translateX(16px)',
        }}
        onClick={handleDelete}
        aria-label="Delete webpage"
      >
        <Trash className="h-5 w-5 text-red-600" weight="bold" />
      </Button>
      
      {/* Main three-dot button */}
      <Button
        variant="secondary"
        className="h-14 w-14 rounded-full p-0 shadow-lg bg-neutral-700 hover:bg-neutral-800 active:bg-neutral-900"
        onClick={handleToggle}
        aria-label={isExpanded ? "Close actions" : "Open actions"}
      >
        {isExpanded ? (
          <X className="h-6 w-6 text-white" weight="bold" />
        ) : (
          <DotsThreeVertical className="h-6 w-6 text-white" weight="bold" />
        )}
      </Button>
    </div>
  );
}
